import React, { useState, useEffect } from 'react';
import Button from './Button';
import { getCreditBalance } from '../services/creditService';
import { User } from 'firebase/auth';

interface CreditPack {
  id: string;
  credits: number;
  price: string;
  priceId: string;
  popular?: boolean;
}

interface CreditConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  creditsRequired: number;
  actionDescription: string;
  currentUser: User;
  onPurchaseCredits: (priceId: string) => Promise<void>;
}

const CREDIT_PACKS: CreditPack[] = [
  {
    id: 'starter',
    credits: 5,
    price: '$5.00 AUD',
    priceId: 'price_1SVnLQ8ZtYxRZ3sb0Mow5mEI',
  },
  {
    id: 'popular',
    credits: 25,
    price: '$10.00 AUD',
    priceId: 'price_1SVnLs8ZtYxRZ3sbWV7c4vp2',
    popular: true,
  },
  {
    id: 'value',
    credits: 200,
    price: '$49.00 AUD',
    priceId: 'price_1SVnN28ZtYxRZ3sbrSrx9hi5',
  },
];

const CreditConfirmationModal: React.FC<CreditConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  creditsRequired,
  actionDescription,
  currentUser,
  onPurchaseCredits,
}) => {
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && currentUser) {
      const fetchBalance = async () => {
        setIsLoading(true);
        try {
          const balance = await getCreditBalance(currentUser.uid, currentUser.email || undefined);
          setCurrentBalance(balance);
        } catch (err) {
          setError('Failed to fetch credit balance');
          console.error('Error fetching credit balance:', err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchBalance();
    }
  }, [isOpen, currentUser]);

  const hasEnoughCredits = currentBalance >= creditsRequired;

  const handleConfirm = () => {
    if (hasEnoughCredits) {
      onConfirm();
    }
  };

  const handlePurchase = async (priceId: string) => {
    setLoadingPriceId(priceId);
    try {
      await onPurchaseCredits(priceId);
    } catch (error) {
      // Error handling will be done by parent component
      setLoadingPriceId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Confirm Action
          </h2>
          <p className="text-gray-600">
            {actionDescription}
          </p>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading credit balance...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : (
            <>
              {/* Credit Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">Credits Required:</span>
                  <span className="text-lg font-bold text-blue-600">{creditsRequired}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">Your Current Balance:</span>
                  <span className={`text-lg font-bold ${hasEnoughCredits ? 'text-green-600' : 'text-red-600'}`}>
                    {currentBalance}
                  </span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Balance After Action:</span>
                  <span className={`text-lg font-bold ${hasEnoughCredits ? 'text-green-600' : 'text-red-600'}`}>
                    {hasEnoughCredits ? currentBalance - creditsRequired : 'Insufficient'}
                  </span>
                </div>
              </div>

              {hasEnoughCredits ? (
                <div className="flex items-center justify-center p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span className="text-green-700 font-medium">You have sufficient credits to proceed!</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
                    <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-red-700 font-medium">
                      You need {creditsRequired - currentBalance} more credits to proceed
                    </span>
                  </div>

                  {/* Credit Packs */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                      Choose a Credit Pack
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {CREDIT_PACKS.map((pack) => (
                        <div
                          key={pack.id}
                          className={`relative border-2 rounded-lg p-4 text-center transition-all duration-200 hover:shadow-md ${
                            pack.popular
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                        >
                          {pack.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                                Most Popular
                              </span>
                            </div>
                          )}
                          <div className="mb-3">
                            <h4 className="text-xl font-bold text-gray-900">{pack.credits} Credits</h4>
                            <p className="text-2xl font-bold text-blue-600 mt-1">{pack.price}</p>
                            <p className="text-xs text-gray-600 mt-1">
                              ~{(parseFloat(pack.price.replace(/[^\d.]/g, '')) / pack.credits).toFixed(2)} AUD per credit
                            </p>
                          </div>
                          <Button
                            onClick={() => handlePurchase(pack.priceId)}
                            disabled={loadingPriceId !== null}
                            className={`w-full ${
                              pack.popular
                                ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400'
                                : 'bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400'
                            } ${loadingPriceId !== null ? 'cursor-not-allowed' : ''}`}
                            size="sm"
                          >
                            {loadingPriceId === pack.priceId ? (
                              <div className="flex items-center justify-center">
                                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Creating checkout...</span>
                              </div>
                            ) : (
                              'Purchase'
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={onClose}
            variant="secondary"
            className="order-2 sm:order-1"
          >
            Cancel
          </Button>
          {hasEnoughCredits && !isLoading && (
            <Button
              onClick={handleConfirm}
              variant="primary"
              className="order-1 sm:order-2"
            >
              Confirm & Proceed
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditConfirmationModal;
